// src/components/Dashboard/ChefApprovalWidget.js
import React, { useState, useEffect } from 'react';
import './ChefApprovalWidget.css';

export default function ChefApprovalWidget() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadApplications();
  }, [filter]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/chefs?status=${filter}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load applications');
      }

      const data = await response.json();
      setApplications(data.chefs || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      setMessage('Error loading applications. Please check your admin token.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (chefId, action, photoUrl = '') => {
    try {
      const response = await fetch('/api/admin/chefs', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || ''}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: chefId,
          action: action,
          photo_url: photoUrl
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update chef status');
      }

      setMessage(`Chef ${action}d successfully!`);
      loadApplications(); // Reload the list
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating chef:', error);
      setMessage('Error updating chef status. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Pending Review', class: 'status-pending' },
      approved: { text: 'Approved', class: 'status-approved' },
      rejected: { text: 'Rejected', class: 'status-rejected' }
    };
    const badge = badges[status] || { text: status, class: 'status-unknown' };
    return <span className={`status-badge ${badge.class}`}>{badge.text}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="chef-approval-widget">
        <div className="widget-header">
          <h3>Chef Applications</h3>
        </div>
        <div className="loading">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="chef-approval-widget">
      <div className="widget-header">
        <h3>Chef Applications</h3>
        <div className="filter-controls">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="status-filter"
          >
            <option value="pending">Pending Review ({applications.filter(a => a.status === 'pending').length})</option>
            <option value="approved">Approved ({applications.filter(a => a.status === 'approved').length})</option>
            <option value="rejected">Rejected ({applications.filter(a => a.status === 'rejected').length})</option>
            <option value="all">All Applications ({applications.length})</option>
          </select>
          <button onClick={loadApplications} className="refresh-btn">Refresh</button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="applications-list">
        {applications.length === 0 ? (
          <div className="no-applications">
            No applications found for the selected filter.
          </div>
        ) : (
          applications.map((chef) => (
            <div key={chef.id} className="application-card">
              <div className="chef-photo">
                {chef.photo_url ? (
                  <img 
                    src={chef.photo_url} 
                    alt={chef.name}
                    className="chef-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <div className="photo-placeholder" style={{ display: chef.photo_url ? 'none' : 'block' }}>
                  📸 No Photo
                </div>
              </div>

              <div className="chef-info">
                <div className="chef-name">{chef.name}</div>
                <div className="chef-details">
                  <div className="detail-item">
                    <strong>City:</strong> {chef.city || 'Not specified'}
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong> {chef.email || 'Not provided'}
                  </div>
                  <div className="detail-item">
                    <strong>Applied:</strong> {formatDate(chef.created_at)}
                  </div>
                </div>
                <div className="chef-status">
                  {getStatusBadge(chef.status)}
                </div>
              </div>

              <div className="action-buttons">
                {chef.status === 'pending' && (
                  <>
                    <button 
                      className="approve-btn"
                      onClick={() => handleApproval(chef.id, 'approve')}
                    >
                      ✅ Approve
                    </button>
                    <button 
                      className="reject-btn"
                      onClick={() => handleApproval(chef.id, 'reject')}
                    >
                      ❌ Reject
                    </button>
                  </>
                )}
                {chef.status !== 'pending' && (
                  <div className="action-note">
                    {chef.status === 'approved' ? 'Chef approved and live on site' : 'Chef application rejected'}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="admin-notes">
        <h4>Review Guidelines:</h4>
        <ul>
          <li><strong>Approve</strong> chefs who have proper culinary experience and professional photos</li>
          <li><strong>Reject</strong> applications that appear incomplete or unprofessional</li>
          <li>Check that the chef's city matches our service areas (Toronto, Boston, New York, etc.)</li>
          <li>Verify email addresses look legitimate</li>
        </ul>
      </div>
    </div>
  );
}
